// Pitch Mailbox - View and manage pitch submissions
import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { 
  Inbox, Mail, MailOpen, Archive, Star, StarOff, 
  ExternalLink, Loader2, ChevronRight, X, Sparkles,
  Building2, User, Briefcase, TrendingUp, Trash2
} from 'lucide-react';
import { getPageSubmissions, updateSubmissionStatus, deleteSubmission } from '../../services/api';
import { toast } from 'sonner';

const STATUS_COLORS = {
  new: { bg: '#EEF2FF', text: '#4F46E5', dot: '#6366F1' },
  reviewed: { bg: '#F0FDF4', text: '#16A34A', dot: '#22C55E' },
  starred: { bg: '#FEF9C3', text: '#CA8A04', dot: '#EAB308' },
  archived: { bg: '#F1F5F9', text: '#64748B', dot: '#94A3B8' }
};

function SubmissionCard({ submission, isSelected, onClick, onStatusChange }) {
  const status = submission.status || 'new';
  const colors = STATUS_COLORS[status] || STATUS_COLORS.new;
  const data = submission.data || {};
  
  return (
    <div
      onClick={onClick}
      style={{
        padding: '16px',
        borderRadius: '12px',
        border: isSelected ? '2px solid #6366F1' : '1px solid #E2E8F0',
        background: isSelected ? '#FAFBFF' : '#FFFFFF',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
      }}
      onMouseOver={(e) => { if (!isSelected) e.currentTarget.style.background = '#F8FAFC'; }}
      onMouseOut={(e) => { if (!isSelected) e.currentTarget.style.background = '#FFFFFF'; }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        {/* Status indicator */}
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: colors.dot,
          marginTop: '6px',
          flexShrink: 0
        }} />
        
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Company name */}
          <div style={{ 
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#0F172A',
            marginBottom: '4px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {data.company_name || 'Unnamed Submission'}
          </div>
          
          {/* One-liner preview or AI Focus summary */}
          <div style={{ 
            fontSize: '13px', 
            color: '#64748B',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginBottom: '8px'
          }}>
            {submission.ai_quick_summary?.focus || data.one_liner || data.email || 'No description'}
          </div>
          
          {/* Tags row */}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
            {data.stage && (
              <span style={{
                padding: '2px 8px',
                background: '#F1F5F9',
                color: '#475569',
                fontSize: '11px',
                fontWeight: '500',
                borderRadius: '100px'
              }}>
                {data.stage}
              </span>
            )}
            {data.sector && !data.business_model && (
              <span style={{
                padding: '2px 8px',
                background: '#F1F5F9',
                color: '#475569',
                fontSize: '11px',
                fontWeight: '500',
                borderRadius: '100px'
              }}>
                {data.sector}
              </span>
            )}
            {data.business_model && (
              <span style={{
                padding: '2px 8px',
                background: '#F1F5F9',
                color: '#475569',
                fontSize: '11px',
                fontWeight: '500',
                borderRadius: '100px'
              }}>
                {data.business_model}
              </span>
            )}
            {submission.ai_score && (
              <span style={{
                padding: '2px 8px',
                background: submission.ai_score >= 70 ? '#DCFCE7' : submission.ai_score >= 40 ? '#FEF9C3' : '#FEE2E2',
                color: submission.ai_score >= 70 ? '#166534' : submission.ai_score >= 40 ? '#854D0E' : '#991B1B',
                fontSize: '11px',
                fontWeight: '600',
                borderRadius: '100px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <Sparkles className="w-3 h-3" />
                {submission.ai_score}
              </span>
            )}
          </div>
        </div>
        
        {/* Time */}
        <div style={{ 
          fontSize: '11px', 
          color: '#94A3B8',
          flexShrink: 0
        }}>
          {formatTimeAgo(submission.submitted_at)}
        </div>
      </div>
    </div>
  );
}

function SubmissionDetail({ submission, onStatusChange, onClose, onDelete }) {
  if (!submission) return null;
  
  const data = submission.data || {};
  const status = submission.status || 'new';
  
  return (
    <div style={{ 
      flex: 1, 
      display: 'flex', 
      flexDirection: 'column',
      background: '#FFFFFF',
      borderLeft: '1px solid #E2E8F0',
      height: '100%',
      minHeight: 0,
      overflow: 'hidden'
    }}>
      {/* Header - Fixed */}
      <div style={{ 
        padding: '16px 20px',
        borderBottom: '1px solid #F1F5F9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        background: '#FFFFFF'
      }}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#0F172A', margin: 0 }}>
            {data.company_name || 'Submission'}
          </h2>
          <p style={{ fontSize: '13px', color: '#64748B', margin: '4px 0 0' }}>
            Submitted {formatTimeAgo(submission.submitted_at)}
          </p>
        </div>
        
        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => onStatusChange(submission.id, status === 'starred' ? 'reviewed' : 'starred')}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              border: 'none',
              background: status === 'starred' ? '#FEF9C3' : '#F1F5F9',
              color: status === 'starred' ? '#CA8A04' : '#64748B',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title={status === 'starred' ? 'Unstar' : 'Star'}
          >
            {status === 'starred' ? <Star className="w-4 h-4" fill="currentColor" /> : <StarOff className="w-4 h-4" />}
          </button>
          <button
            onClick={() => onStatusChange(submission.id, 'archived')}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              border: 'none',
              background: '#F1F5F9',
              color: '#64748B',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Archive"
          >
            <Archive className="w-4 h-4" />
          </button>
          <button
            onClick={() => {
              if (window.confirm('Are you sure you want to permanently delete this submission?')) {
                onDelete(submission.id);
              }
            }}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              border: 'none',
              background: '#FEE2E2',
              color: '#DC2626',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <button
            onClick={onClose}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              border: 'none',
              background: '#F1F5F9',
              color: '#64748B',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Content - Scrollable */}
      <div style={{ 
        flex: 1, 
        overflowY: 'auto', 
        overflowX: 'hidden',
        padding: '20px', 
        minHeight: 0 
      }}>
        {/* AI Summary */}
        {(submission.ai_executive_summary || submission.ai_quick_summary || submission.ai_summary || submission.ai_score_breakdown) && (
          <div style={{
            padding: '16px',
            background: 'linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 100%)',
            borderRadius: '12px',
            marginBottom: '24px'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              marginBottom: '12px'
            }}>
              <Sparkles className="w-4 h-4" style={{ color: '#6366F1' }} />
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#4F46E5' }}>
                AI Assessment
              </span>
              {submission.ai_processing ? (
                <span style={{
                  marginLeft: 'auto',
                  padding: '4px 12px',
                  background: '#E0E7FF',
                  color: '#4338CA',
                  fontSize: '13px',
                  fontWeight: '600',
                  borderRadius: '100px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Analyzing...
                </span>
              ) : submission.ai_score ? (
                <span style={{
                  marginLeft: 'auto',
                  padding: '4px 12px',
                  background: submission.ai_score >= 70 ? '#DCFCE7' : submission.ai_score >= 40 ? '#FEF9C3' : '#FEE2E2',
                  color: submission.ai_score >= 70 ? '#166534' : submission.ai_score >= 40 ? '#854D0E' : '#991B1B',
                  fontSize: '14px',
                  fontWeight: '700',
                  borderRadius: '100px'
                }}>
                  {submission.ai_score}/100
                </span>
              ) : null}
            </div>
            
            {/* Processing indicator */}
            {submission.ai_processing && (
              <div style={{
                padding: '16px',
                background: 'rgba(99, 102, 241, 0.08)',
                borderRadius: '8px',
                textAlign: 'center',
                color: '#4338CA'
              }}>
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                <p style={{ margin: 0, fontSize: '13px', fontWeight: '500' }}>
                  AI is analyzing this pitch...
                </p>
                <p style={{ margin: '4px 0 0', fontSize: '12px', opacity: 0.7 }}>
                  This usually takes 15-30 seconds
                </p>
              </div>
            )}
            
            {/* Executive Summary - Top */}
            {!submission.ai_processing && submission.ai_executive_summary && (
              <div style={{
                padding: '12px',
                background: 'rgba(99, 102, 241, 0.1)',
                borderRadius: '8px',
                marginBottom: '12px',
                borderLeft: '3px solid #6366F1'
              }}>
                <p style={{ 
                  fontSize: '14px', 
                  color: '#1E1B4B', 
                  lineHeight: '1.6', 
                  margin: 0,
                  fontWeight: '500'
                }}>
                  {submission.ai_executive_summary}
                </p>
              </div>
            )}
            
            {/* Quick Summary - Structured */}
            {!submission.ai_processing && submission.ai_quick_summary && Object.keys(submission.ai_quick_summary).length > 0 && (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '8px',
                marginBottom: '12px'
              }}>
                {submission.ai_quick_summary.founders && (
                  <QuickSummaryLine icon="👥" label="Founders" value={submission.ai_quick_summary.founders} />
                )}
                {submission.ai_quick_summary.founder && !submission.ai_quick_summary.founders && (
                  <QuickSummaryLine icon="👤" label="Founder" value={submission.ai_quick_summary.founder} />
                )}
                {submission.ai_quick_summary.founder_market_fit && (
                  <QuickSummaryLine icon="🎯" label="Founder-Market Fit" value={submission.ai_quick_summary.founder_market_fit} />
                )}
                {submission.ai_quick_summary.focus && (
                  <QuickSummaryLine icon="💡" label="Focus" value={submission.ai_quick_summary.focus} />
                )}
                {submission.ai_quick_summary.traction && (
                  <QuickSummaryLine icon="📈" label="Traction" value={submission.ai_quick_summary.traction} />
                )}
                {submission.ai_quick_summary.fit && (
                  <QuickSummaryLine icon="✅" label="Fit" value={submission.ai_quick_summary.fit} />
                )}
              </div>
            )}
            
            {/* Fallback to old summary format */}
            {!submission.ai_processing && !submission.ai_quick_summary && submission.ai_summary && (
              <p style={{ fontSize: '13px', color: '#4338CA', lineHeight: '1.6', margin: '0 0 12px 0' }}>
                {submission.ai_summary}
              </p>
            )}
            
            {/* Sector & Problem/Solution */}
            {!submission.ai_processing && submission.ai_sector_info && (
              <div style={{ 
                marginTop: '12px',
                paddingTop: '12px',
                borderTop: '1px solid rgba(99, 102, 241, 0.2)'
              }}>
                {/* Sector Tags */}
                {(submission.ai_sector_info.sector || submission.ai_sector_info.subsector) && (
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    {submission.ai_sector_info.sector && (
                      <span style={{
                        padding: '4px 10px',
                        background: '#EEF2FF',
                        color: '#4338CA',
                        fontSize: '12px',
                        fontWeight: '600',
                        borderRadius: '100px'
                      }}>
                        {submission.ai_sector_info.sector}
                      </span>
                    )}
                    {submission.ai_sector_info.subsector && (
                      <span style={{
                        padding: '4px 10px',
                        background: '#F0FDF4',
                        color: '#166534',
                        fontSize: '12px',
                        fontWeight: '500',
                        borderRadius: '100px'
                      }}>
                        {submission.ai_sector_info.subsector}
                      </span>
                    )}
                  </div>
                )}
                
                {/* Problem */}
                {submission.ai_sector_info.problem && (
                  <div style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#6366F1', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Problem
                    </div>
                    <p style={{ fontSize: '13px', color: '#374151', lineHeight: '1.5', margin: 0 }}>
                      {submission.ai_sector_info.problem}
                    </p>
                  </div>
                )}
                
                {/* Solution */}
                {submission.ai_sector_info.solution && (
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#6366F1', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Solution
                    </div>
                    <p style={{ fontSize: '13px', color: '#374151', lineHeight: '1.5', margin: 0 }}>
                      {submission.ai_sector_info.solution}
                    </p>
                  </div>
                )}
              </div>
            )}
            
            {/* Next Steps - Highlighted */}
            {!submission.ai_processing && submission.ai_next_steps && (
              <div style={{ 
                marginTop: '12px',
                padding: '12px',
                background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
                borderRadius: '8px',
                border: '1px solid #F59E0B'
              }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#92400E', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  ⚡ Recommended Next Steps
                </div>
                <div style={{ 
                  fontSize: '13px', 
                  color: '#78350F', 
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap'
                }}>
                  {submission.ai_next_steps}
                </div>
              </div>
            )}
            
            {/* Score Breakdown */}
            {!submission.ai_processing && submission.ai_score_breakdown && Object.keys(submission.ai_score_breakdown).length > 0 && (
              <div style={{ 
                marginTop: '12px',
                paddingTop: '12px',
                borderTop: '1px solid rgba(99, 102, 241, 0.2)'
              }}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: '#6366F1', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Score Breakdown
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {Object.entries(submission.ai_score_breakdown).map(([key, value]) => (
                    <ScoreBreakdownItem key={key} label={formatBreakdownLabel(key)} score={value.score} reason={value.reason} />
                  ))}
                </div>
              </div>
            )}
            
            {/* Detailed Notes Section */}
            {!submission.ai_processing && submission.ai_notes && (
              <div style={{ 
                marginTop: '12px',
                paddingTop: '12px',
                borderTop: '1px solid rgba(99, 102, 241, 0.2)'
              }}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: '#6366F1', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Detailed Analysis
                </div>
                <div style={{ 
                  fontSize: '13px', 
                  color: '#4338CA', 
                  lineHeight: '1.7',
                  whiteSpace: 'pre-wrap'
                }}>
                  {submission.ai_notes}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Company Info */}
          <FieldGroup icon={<Building2 className="w-4 h-4" />} title="Company">
            <FieldItem label="Company Name" value={data.company_name} />
            <FieldItem label="One-Liner" value={data.one_liner} multiline />
            {data.sector && <FieldItem label="Sector" value={data.sector} />}
          </FieldGroup>
          
          {/* Business Details */}
          <FieldGroup icon={<Briefcase className="w-4 h-4" />} title="Business">
            {data.stage && <FieldItem label="Stage" value={data.stage} />}
            {data.business_model && <FieldItem label="Business Model" value={data.business_model} />}
          </FieldGroup>
          
          {/* Contact */}
          <FieldGroup icon={<User className="w-4 h-4" />} title="Contact">
            <FieldItem label="Email" value={data.email} isLink={data.email?.includes('@')} />
            {data.founder_linkedin && (
              <FieldItem 
                label="LinkedIn" 
                value={data.founder_linkedin} 
                isLink 
              />
            )}
          </FieldGroup>
          
          {/* Uploaded Files / Pitch Deck */}
          {(submission.files && submission.files.length > 0) || data.deck_url ? (
            <FieldGroup icon={<ExternalLink className="w-4 h-4" />} title="Pitch Deck">
              {/* Show files from array */}
              {submission.files && submission.files.map((file, idx) => (
                <FileItem key={idx} file={file} />
              ))}
              {/* Show deck_url from form data if no files */}
              {(!submission.files || submission.files.length === 0) && data.deck_url && (
                <FileItem file={{ url: data.deck_url, name: 'Pitch Deck' }} />
              )}
            </FieldGroup>
          ) : null}
          
          {/* Notes */}
          {data.additional_notes && (
            <FieldGroup icon={<TrendingUp className="w-4 h-4" />} title="Additional Notes">
              <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6', margin: 0 }}>
                {data.additional_notes}
              </p>
            </FieldGroup>
          )}
        </div>
      </div>
      
      {/* Footer Actions - Fixed */}
      <div style={{ 
        padding: '16px 24px',
        borderTop: '1px solid #F1F5F9',
        display: 'flex',
        gap: '12px',
        flexShrink: 0
      }}>
        {data.email && (
          <Button
            onClick={() => window.location.href = `mailto:${data.email}`}
            className="flex-1 bg-indigo-600 hover:bg-indigo-700"
          >
            <Mail className="w-4 h-4 mr-2" />
            Reply via Email
          </Button>
        )}
        {status === 'new' && (
          <Button
            variant="outline"
            onClick={() => onStatusChange(submission.id, 'reviewed')}
          >
            <MailOpen className="w-4 h-4 mr-2" />
            Mark Reviewed
          </Button>
        )}
      </div>
    </div>
  );
}

function FieldGroup({ icon, title, children }) {
  return (
    <div>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px', 
        marginBottom: '12px',
        color: '#6366F1'
      }}>
        {icon}
        <span style={{ fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {title}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '24px' }}>
        {children}
      </div>
    </div>
  );
}

function ScoreBreakdownItem({ label, score, reason }) {
  const getScoreColor = (s) => {
    if (s >= 70) return { bg: '#DCFCE7', text: '#166534' };
    if (s >= 40) return { bg: '#FEF9C3', text: '#854D0E' };
    return { bg: '#FEE2E2', text: '#991B1B' };
  };
  
  const colors = getScoreColor(score);
  
  return (
    <div style={{
      padding: '8px 10px',
      background: 'rgba(255, 255, 255, 0.6)',
      borderRadius: '8px',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '11px', fontWeight: '600', color: '#475569' }}>{label}</span>
        <span style={{
          padding: '2px 6px',
          background: colors.bg,
          color: colors.text,
          fontSize: '10px',
          fontWeight: '700',
          borderRadius: '4px'
        }}>
          {score}
        </span>
      </div>
      {reason && (
        <span style={{ fontSize: '10px', color: '#64748B', lineHeight: '1.4' }}>
          {reason}
        </span>
      )}
    </div>
  );
}

function QuickSummaryLine({ icon, label, value }) {
  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'flex-start', 
      gap: '8px',
      fontSize: '13px',
      lineHeight: '1.5'
    }}>
      <span style={{ flexShrink: 0 }}>{icon}</span>
      <div>
        <span style={{ fontWeight: '600', color: '#4338CA', marginRight: '6px' }}>{label}:</span>
        <span style={{ color: '#5B21B6' }}>{value}</span>
      </div>
    </div>
  );
}

function formatBreakdownLabel(key) {
  const labels = {
    'industry_fit': 'Industry Fit',
    'stage_fit': 'Stage Fit',
    'custom_criteria': 'Custom Criteria',
    'overall_potential': 'Overall Potential'
  };
  return labels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function FieldItem({ label, value, isLink, multiline }) {
  if (!value) return null;
  
  return (
    <div>
      <span style={{ fontSize: '11px', color: '#94A3B8', display: 'block', marginBottom: '2px' }}>
        {label}
      </span>
      {isLink ? (
        <a 
          href={value.startsWith('http') ? value : (value.includes('@') ? `mailto:${value}` : `https://${value}`)}
          target="_blank"
          rel="noopener noreferrer"
          style={{ 
            fontSize: '14px', 
            color: '#4F46E5', 
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          {value}
          <ExternalLink className="w-3 h-3" />
        </a>
      ) : (
        <span style={{ 
          fontSize: '14px', 
          color: '#1F2937',
          lineHeight: multiline ? '1.6' : '1.4'
        }}>
          {value}
        </span>
      )}
    </div>
  );
}

function FileItem({ file }) {
  // Handle both URL string and object with url/name
  const fileUrl = typeof file === 'string' ? file : file.url;
  const fileName = typeof file === 'string' 
    ? file.split('/').pop().split('?')[0] 
    : (file.name || file.url?.split('/').pop().split('?')[0] || 'File');
  
  const isPdf = fileName?.toLowerCase().endsWith('.pdf') || fileUrl?.includes('.pdf');
  
  if (!fileUrl) return null;
  
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px',
      background: '#F8FAFC',
      borderRadius: '8px',
      border: '1px solid #E2E8F0'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: '8px',
        background: isPdf ? '#FEE2E2' : '#E0E7FF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        {isPdf ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14,2 14,8 20,8"/>
            <path d="M9 13h6"/>
            <path d="M9 17h6"/>
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14,2 14,8 20,8"/>
          </svg>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ 
          fontSize: '13px', 
          fontWeight: '500', 
          color: '#1F2937',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {fileName}
        </div>
        <div style={{ fontSize: '11px', color: '#94A3B8' }}>
          {isPdf ? 'PDF Document' : 'File'}
        </div>
      </div>
      <a
        href={fileUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          padding: '6px 12px',
          background: '#4F46E5',
          color: '#FFFFFF',
          fontSize: '12px',
          fontWeight: '500',
          borderRadius: '6px',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}
      >
        View
        <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
}

function formatTimeAgo(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function PitchMailbox({ pageId, isOpen, onClose }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState('all'); // all, new, starred, archived

  const loadSubmissions = useCallback(async () => {
    if (!pageId) return;
    try {
      setLoading(true);
      const data = await getPageSubmissions(pageId);
      setSubmissions(data.submissions || []);
      // Auto-select first if none selected
      if (data.submissions?.length > 0 && !selectedId) {
        setSelectedId(data.submissions[0].id);
      }
    } catch (err) {
      console.error('Failed to load submissions:', err);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [pageId, selectedId]);

  useEffect(() => {
    if (isOpen) {
      loadSubmissions();
    }
  }, [isOpen, loadSubmissions]);

  // Auto-mark as reviewed when selecting a submission
  const handleSelectSubmission = async (submissionId) => {
    setSelectedId(submissionId);
    const submission = submissions.find(s => s.id === submissionId);
    if (submission && submission.status === 'new') {
      try {
        await updateSubmissionStatus(submissionId, 'reviewed');
        setSubmissions(prev => prev.map(s => 
          s.id === submissionId ? { ...s, status: 'reviewed' } : s
        ));
      } catch (err) {
        console.error('Failed to mark as reviewed:', err);
      }
    }
  };

  const handleStatusChange = async (submissionId, newStatus) => {
    try {
      await updateSubmissionStatus(submissionId, newStatus);
      setSubmissions(prev => prev.map(s => 
        s.id === submissionId ? { ...s, status: newStatus } : s
      ));
      toast.success(`Marked as ${newStatus}`);
    } catch (err) {
      console.error('Failed to update status:', err);
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (submissionId) => {
    try {
      await deleteSubmission(submissionId);
      setSubmissions(prev => prev.filter(s => s.id !== submissionId));
      // Select next submission or clear selection
      const remaining = submissions.filter(s => s.id !== submissionId);
      if (remaining.length > 0) {
        setSelectedId(remaining[0].id);
      } else {
        setSelectedId(null);
      }
      toast.success('Submission deleted');
    } catch (err) {
      console.error('Failed to delete submission:', err);
      toast.error('Failed to delete');
    }
  };

  const filteredSubmissions = submissions.filter(s => {
    if (filter === 'all') return s.status !== 'archived';
    return s.status === filter;
  });

  const selectedSubmission = submissions.find(s => s.id === selectedId);

  const counts = {
    all: submissions.filter(s => s.status !== 'archived').length,
    new: submissions.filter(s => s.status === 'new').length,
    starred: submissions.filter(s => s.status === 'starred').length,
    archived: submissions.filter(s => s.status === 'archived').length
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent 
        className="sm:max-w-5xl p-0 gap-0 overflow-hidden [&>button]:hidden"
        style={{ height: '85vh', maxHeight: '800px' }}
      >
        <DialogTitle className="sr-only">Pitch Inbox</DialogTitle>
        <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
          {/* Sidebar */}
          <div style={{ 
            width: '320px', 
            borderRight: '1px solid #E2E8F0',
            display: 'flex',
            flexDirection: 'column',
            background: '#FAFBFC'
          }}>
            {/* Header */}
            <div style={{ 
              padding: '20px',
              borderBottom: '1px solid #E2E8F0',
              background: '#FFFFFF'
            }}>
              <h2 style={{ 
                fontSize: '16px', 
                fontWeight: '600', 
                color: '#0F172A',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Inbox className="w-5 h-5" style={{ color: '#6366F1' }} />
                Pitch Inbox
              </h2>
              <p style={{ fontSize: '12px', color: '#64748B', margin: '4px 0 0' }}>
                {counts.all} {counts.all === 1 ? 'submission' : 'submissions'}
                {counts.new > 0 && ` · ${counts.new} new`}
              </p>
            </div>
            
            {/* Filters */}
            <div style={{ 
              display: 'flex', 
              gap: '4px', 
              padding: '12px 16px',
              borderBottom: '1px solid #F1F5F9'
            }}>
              {[
                { key: 'all', label: 'All', count: counts.all },
                { key: 'new', label: 'New', count: counts.new },
                { key: 'starred', label: 'Starred', count: counts.starred },
                { key: 'archived', label: 'Archived', count: counts.archived }
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: 'none',
                    background: filter === f.key ? '#EEF2FF' : 'transparent',
                    color: filter === f.key ? '#4F46E5' : '#64748B',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                >
                  {f.label}
                  {f.count > 0 && (
                    <span style={{ 
                      marginLeft: '4px',
                      opacity: 0.7
                    }}>
                      {f.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
            
            {/* List */}
            <div style={{ flex: 1, overflow: 'auto', padding: '8px' }}>
              {loading ? (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  padding: '40px'
                }}>
                  <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#6366F1' }} />
                </div>
              ) : filteredSubmissions.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px 20px',
                  color: '#94A3B8'
                }}>
                  <Inbox className="w-10 h-10 mx-auto mb-3" style={{ opacity: 0.4 }} />
                  <p style={{ fontSize: '14px', fontWeight: '500', margin: 0 }}>
                    {filter === 'all' ? 'No pitches yet' : `No ${filter} pitches`}
                  </p>
                  <p style={{ fontSize: '12px', margin: '4px 0 0' }}>
                    {filter === 'all' 
                      ? 'Pitches will appear here when someone submits' 
                      : 'Change filter to see other submissions'}
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {filteredSubmissions.map(submission => (
                    <SubmissionCard
                      key={submission.id}
                      submission={submission}
                      isSelected={selectedId === submission.id}
                      onClick={() => handleSelectSubmission(submission.id)}
                      onStatusChange={handleStatusChange}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Detail Panel */}
          {selectedSubmission ? (
            <SubmissionDetail
              submission={selectedSubmission}
              onStatusChange={handleStatusChange}
              onClose={() => setSelectedId(null)}
              onDelete={handleDelete}
            />
          ) : (
            <div style={{ 
              flex: 1, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              background: '#FAFBFC'
            }}>
              <div style={{ textAlign: 'center', color: '#94A3B8' }}>
                <Mail className="w-12 h-12 mx-auto mb-3" style={{ opacity: 0.3 }} />
                <p style={{ fontSize: '14px', fontWeight: '500' }}>Select a pitch to view details</p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
